// Diálogos de la ficha de campaña: agregar tarea, confirmar desglose, pedir o
// mover fecha, agregar material y entregar liga. Cada diálogo monta su
// formulario al abrirse (Radix desmonta el contenido al cerrar), así que el
// estado inicial sale de las props sin efectos.
import * as React from "react";
import { Trash2 } from "lucide-react";
import {
  Boton,
  Campo,
  Dialog,
  DialogContenido,
  DialogDescripcion,
  DialogEncabezado,
  DialogPie,
  DialogTitulo,
  Input,
  Select,
  SelectContenido,
  SelectDisparador,
  SelectOpcion,
  SelectValor,
  Textarea,
} from "@iavmx/ui";
import type { Catalogo, Desglose, TareaFicha } from "./consultas";
import { Nota } from "./componentes";
import { fechaCorta, sumarDias } from "./util";

const SIN = "__sin__";

export interface NuevaTarea {
  titulo: string;
  descripcion: string | null;
  areaResponsableId: string | null;
  responsableId: string | null;
  fechaLimite: string | null;
  dependeDe: string[];
}

interface PropsTarea {
  catalogo: Catalogo;
  tareas: TareaFicha[];
  fechaPublicacion: string;
  inicial?: Partial<NuevaTarea> & { id?: string };
  titulo: string;
  onGuardar: (t: NuevaTarea) => void;
  guardando: boolean;
  onCerrar: () => void;
}

export function DialogoTarea({ abierto, ...props }: PropsTarea & { abierto: boolean }) {
  return (
    <Dialog open={abierto} onOpenChange={(o) => !o && props.onCerrar()}>
      <DialogContenido className="max-h-[90vh] overflow-y-auto sm:max-w-xl">
        <FormularioTarea key={props.inicial?.id ?? "nueva"} {...props} />
      </DialogContenido>
    </Dialog>
  );
}

function FormularioTarea({ catalogo, tareas, fechaPublicacion, inicial, titulo, onGuardar, guardando, onCerrar }: PropsTarea) {
  const [t, setT] = React.useState<NuevaTarea>(() => ({
    titulo: inicial?.titulo ?? "",
    descripcion: inicial?.descripcion ?? null,
    areaResponsableId: inicial?.areaResponsableId ?? null,
    responsableId: inicial?.responsableId ?? null,
    fechaLimite: inicial?.fechaLimite ?? null,
    dependeDe: inicial?.dependeDe ?? [],
  }));
  const gente = t.areaResponsableId ? catalogo.personas.filter((p) => p.areaId === t.areaResponsableId) : catalogo.personas;
  const fechaMala = Boolean(t.fechaLimite && t.fechaLimite >= fechaPublicacion);
  const otras = tareas.filter((x) => x.id !== inicial?.id);
  return (
    <>
      <DialogEncabezado>
        <DialogTitulo>{titulo}</DialogTitulo>
        <DialogDescripcion>La fecha límite va antes de la publicación ({fechaCorta(fechaPublicacion)}). Si depende de otra tarea, no se podrá terminar hasta recibir y validar su material.</DialogDescripcion>
      </DialogEncabezado>
      <form
        className="grid gap-4"
        onSubmit={(e) => {
          e.preventDefault();
          if (!t.titulo.trim() || fechaMala) return;
          onGuardar(t);
        }}
      >
        <Campo etiqueta="Tarea" htmlFor="t-titulo">
          <Input id="t-titulo" value={t.titulo} onChange={(e) => setT({ ...t, titulo: e.target.value })} placeholder="Video institucional 90 s" className="text-base" required />
        </Campo>
        <div className="grid gap-4 sm:grid-cols-2">
          <Campo etiqueta="Área responsable" htmlFor="t-area">
            <Select value={t.areaResponsableId ?? SIN} onValueChange={(v) => setT({ ...t, areaResponsableId: v === SIN ? null : v, responsableId: null })}>
              <SelectDisparador id="t-area" className="text-base">
                <SelectValor placeholder="Área" />
              </SelectDisparador>
              <SelectContenido>
                <SelectOpcion value={SIN}>Sin área</SelectOpcion>
                {catalogo.areas.map((a) => (
                  <SelectOpcion key={a.id} value={a.id}>
                    {a.nombre}
                  </SelectOpcion>
                ))}
              </SelectContenido>
            </Select>
          </Campo>
          <Campo etiqueta="Responsable" htmlFor="t-resp" ayuda="Recibe el recordatorio en la fecha límite.">
            <Select value={t.responsableId ?? SIN} onValueChange={(v) => setT({ ...t, responsableId: v === SIN ? null : v })}>
              <SelectDisparador id="t-resp" className="text-base">
                <SelectValor placeholder="Persona" />
              </SelectDisparador>
              <SelectContenido>
                <SelectOpcion value={SIN}>Sin persona (el área decide)</SelectOpcion>
                {gente.map((p) => (
                  <SelectOpcion key={p.id} value={p.id}>
                    {p.nombre}
                  </SelectOpcion>
                ))}
              </SelectContenido>
            </Select>
          </Campo>
        </div>
        <Campo etiqueta="Fecha límite" htmlFor="t-fecha" error={fechaMala ? "Debe ser anterior a la fecha de publicación." : undefined}>
          <Input id="t-fecha" type="date" max={sumarDias(fechaPublicacion, -1)} value={t.fechaLimite ?? ""} onChange={(e) => setT({ ...t, fechaLimite: e.target.value || null })} className="max-w-xs text-base" />
        </Campo>
        <Campo etiqueta="Descripción (opcional)" htmlFor="t-desc">
          <Textarea id="t-desc" value={t.descripcion ?? ""} onChange={(e) => setT({ ...t, descripcion: e.target.value || null })} className="min-h-16 text-base" />
        </Campo>
        {otras.length > 0 && (
          <fieldset>
            <legend className="mb-1.5 font-ui text-xs font-semibold uppercase tracking-wide text-tinta-3">Depende de</legend>
            <ul className="grid max-h-40 gap-1 overflow-y-auto border border-linea p-2">
              {otras.map((x) => (
                <li key={x.id}>
                  <label className="flex min-h-11 items-center gap-2 font-ui text-sm md:min-h-9">
                    <input
                      type="checkbox"
                      className="size-4 accent-tinta"
                      checked={t.dependeDe.includes(x.id)}
                      onChange={(e) => setT({ ...t, dependeDe: e.target.checked ? [...t.dependeDe, x.id] : t.dependeDe.filter((y) => y !== x.id) })}
                    />
                    {x.titulo}
                    <span className="text-tinta-tenue">· {x.areaResponsable ?? "sin área"}</span>
                  </label>
                </li>
              ))}
            </ul>
          </fieldset>
        )}
        <DialogPie>
          <Boton type="button" variante="contorno" onClick={onCerrar}>
            Cancelar
          </Boton>
          <Boton type="submit" variante="acento" disabled={guardando || !t.titulo.trim() || fechaMala}>
            {guardando ? "Guardando…" : "Guardar tarea"}
          </Boton>
        </DialogPie>
      </form>
    </>
  );
}

interface PropsDesglose {
  propuesta: Desglose;
  onCerrar: () => void;
  catalogo: Catalogo;
  fechaPublicacion: string;
  onConfirmar: (tareas: Desglose["desglose"]["tareas"]) => void;
  guardando: boolean;
}

export function DialogoDesglose({ propuesta, ...props }: Omit<PropsDesglose, "propuesta"> & { propuesta: Desglose | null }) {
  return (
    <Dialog open={Boolean(propuesta)} onOpenChange={(o) => !o && props.onCerrar()}>
      <DialogContenido className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
        {propuesta && <ListaDesglose key={propuesta.propuestaId} propuesta={propuesta} {...props} />}
      </DialogContenido>
    </Dialog>
  );
}

function ListaDesglose({ propuesta, onCerrar, catalogo, fechaPublicacion, onConfirmar, guardando }: PropsDesglose) {
  const [lista, setLista] = React.useState<Desglose["desglose"]["tareas"]>(() => propuesta.desglose.tareas);
  const nombreArea = (clave: string | null) => catalogo.areas.find((a) => a.clave === clave)?.nombre ?? "Sin área";
  const quitar = (i: number) =>
    setLista(lista.filter((_, j) => j !== i).map((t) => ({ ...t, dependeDe: t.dependeDe.filter((d) => d !== i).map((d) => (d > i ? d - 1 : d)) })));
  const fechaMala = lista.some((t) => t.fechaLimite && t.fechaLimite >= fechaPublicacion);
  return (
    <>
      <DialogEncabezado>
        <DialogTitulo>Desglose propuesto</DialogTitulo>
        <DialogDescripcion>
          {propuesta.sinIa ? "Salió de reglas fijas, sin modelo de IA. " : "Lo propuso la IA. "}
          Ajusta fechas y áreas, quita lo que sobre y confirma. Las personas las asignas después, tarea por tarea.
        </DialogDescripcion>
      </DialogEncabezado>
      {propuesta.sinIa && <Nota tono="ambar">{propuesta.motivoSinIa}</Nota>}
      <ol className="grid gap-2">
        {lista.map((t, i) => (
          <li key={i} className="grid gap-2 border border-linea p-2 sm:grid-cols-[auto_1fr_150px_150px_auto] sm:items-center">
            <span className="font-ui text-xs text-tinta-tenue">{i + 1}</span>
            <div className="min-w-0">
              <Input aria-label={`Título de la tarea ${i + 1}`} value={t.titulo} onChange={(e) => setLista(lista.map((x, j) => (j === i ? { ...x, titulo: e.target.value } : x)))} className="text-base" />
              {t.dependeDe.length > 0 && <p className="mt-1 font-ui text-xs text-tinta-tenue">Depende de {t.dependeDe.map((d) => `${d + 1}`).join(", ")}</p>}
            </div>
            <Select value={t.area ?? SIN} onValueChange={(v) => setLista(lista.map((x, j) => (j === i ? { ...x, area: v === SIN ? null : v } : x)))}>
              <SelectDisparador aria-label={`Área de ${t.titulo}`} className="text-base">
                <SelectValor>{nombreArea(t.area)}</SelectValor>
              </SelectDisparador>
              <SelectContenido>
                <SelectOpcion value={SIN}>Sin área</SelectOpcion>
                {catalogo.areas.map((a) => (
                  <SelectOpcion key={a.id} value={a.clave}>
                    {a.nombre}
                  </SelectOpcion>
                ))}
              </SelectContenido>
            </Select>
            <Input aria-label={`Fecha límite de ${t.titulo}`} type="date" max={sumarDias(fechaPublicacion, -1)} value={t.fechaLimite ?? ""} onChange={(e) => setLista(lista.map((x, j) => (j === i ? { ...x, fechaLimite: e.target.value || null } : x)))} className="text-base" />
            <Boton type="button" variante="fantasma" tamano="icono" aria-label={`Quitar ${t.titulo}`} onClick={() => quitar(i)}>
              <Trash2 />
            </Boton>
          </li>
        ))}
      </ol>
      {fechaMala && <p className="font-ui text-xs text-rojo">Hay fechas en o después de la publicación ({fechaCorta(fechaPublicacion)}).</p>}
      <DialogPie>
        <Boton variante="contorno" onClick={onCerrar}>
          Descartar
        </Boton>
        <Boton variante="acento" disabled={guardando || lista.length === 0 || fechaMala || lista.some((t) => !t.titulo.trim())} onClick={() => onConfirmar(lista)}>
          {guardando ? "Creando…" : `Confirmar ${lista.length} tareas`}
        </Boton>
      </DialogPie>
    </>
  );
}

interface PropsFecha {
  tarea: TareaFicha;
  onCerrar: () => void;
  fechaPublicacion: string;
  esMercadotecnia: boolean;
  onGuardar: (fecha: string, motivo: string) => void;
  guardando: boolean;
}

/** Otra área pide mover una fecha; Mercadotecnia la mueve directo. */
export function DialogoFecha({ tarea, ...props }: Omit<PropsFecha, "tarea"> & { tarea: TareaFicha | null }) {
  return (
    <Dialog open={Boolean(tarea)} onOpenChange={(o) => !o && props.onCerrar()}>
      <DialogContenido>{tarea && <FormularioFecha key={tarea.id} tarea={tarea} {...props} />}</DialogContenido>
    </Dialog>
  );
}

function FormularioFecha({ tarea, onCerrar, fechaPublicacion, esMercadotecnia, onGuardar, guardando }: PropsFecha) {
  const [fecha, setFecha] = React.useState(tarea.fechaLimite ?? "");
  const [motivo, setMotivo] = React.useState("");
  const mala = Boolean(fecha && fecha >= fechaPublicacion);
  return (
    <>
      <DialogEncabezado>
        <DialogTitulo>{esMercadotecnia ? "Mover la fecha límite" : "Pedir otra fecha"}</DialogTitulo>
        <DialogDescripcion>
          {tarea.titulo} · hoy vence {tarea.fechaLimite ? fechaCorta(tarea.fechaLimite) : "sin fecha"}.{" "}
          {esMercadotecnia ? "El cambio queda en el historial con tu nombre." : "Mercadotecnia lo aprueba o rechaza; mientras, la fecha no cambia."}
        </DialogDescripcion>
      </DialogEncabezado>
      <form
        className="grid gap-4"
        onSubmit={(e) => {
          e.preventDefault();
          if (!fecha || mala) return;
          onGuardar(fecha, motivo);
        }}
      >
        <Campo etiqueta="Nueva fecha límite" htmlFor="f-fecha" error={mala ? "Debe ser anterior a la publicación." : undefined}>
          <Input id="f-fecha" type="date" max={sumarDias(fechaPublicacion, -1)} value={fecha} onChange={(e) => setFecha(e.target.value)} className="max-w-xs text-base" required />
        </Campo>
        <Campo etiqueta="Por qué" htmlFor="f-motivo">
          <Textarea id="f-motivo" value={motivo} onChange={(e) => setMotivo(e.target.value)} placeholder="Se recorrió la grabación por disponibilidad del director." className="min-h-16 text-base" />
        </Campo>
        <DialogPie>
          <Boton type="button" variante="contorno" onClick={onCerrar}>
            Cancelar
          </Boton>
          <Boton type="submit" variante="acento" disabled={guardando || !fecha || mala}>
            {guardando ? "Guardando…" : esMercadotecnia ? "Mover fecha" : "Pedir cambio"}
          </Boton>
        </DialogPie>
      </form>
    </>
  );
}

export interface NuevoMaterial {
  nombre: string;
  tipo: string;
  areaProductoraId: string | null;
  tareaId: string | null;
  fechaLimite: string | null;
}

interface PropsMaterial {
  onCerrar: () => void;
  catalogo: Catalogo;
  tareas: TareaFicha[];
  fechaPublicacion: string;
  onGuardar: (m: NuevoMaterial) => void;
  guardando: boolean;
}

export function DialogoMaterial({ abierto, ...props }: PropsMaterial & { abierto: boolean }) {
  return (
    <Dialog open={abierto} onOpenChange={(o) => !o && props.onCerrar()}>
      <DialogContenido>
        <FormularioMaterial {...props} />
      </DialogContenido>
    </Dialog>
  );
}

function FormularioMaterial({ onCerrar, catalogo, tareas, fechaPublicacion, onGuardar, guardando }: PropsMaterial) {
  const [m, setM] = React.useState<NuevoMaterial>({ nombre: "", tipo: "otro", areaProductoraId: null, tareaId: null, fechaLimite: null });
  const mala = Boolean(m.fechaLimite && m.fechaLimite >= fechaPublicacion);
  return (
    <>
      <DialogEncabezado>
        <DialogTitulo>Pedir material</DialogTitulo>
        <DialogDescripcion>Logotipos, listas, precios, autorizaciones o una pieza más. Queda como pendiente hasta que alguien entregue la liga.</DialogDescripcion>
      </DialogEncabezado>
      <form
        className="grid gap-4"
        onSubmit={(e) => {
          e.preventDefault();
          if (!m.nombre.trim() || mala) return;
          onGuardar(m);
        }}
      >
        <Campo etiqueta="Material" htmlFor="m-nombre">
          <Input id="m-nombre" value={m.nombre} onChange={(e) => setM({ ...m, nombre: e.target.value })} placeholder="Lista de patrocinadores confirmados" className="text-base" required />
        </Campo>
        <div className="grid gap-4 sm:grid-cols-2">
          <Campo etiqueta="Tipo" htmlFor="m-tipo">
            <Select value={m.tipo} onValueChange={(v) => setM({ ...m, tipo: v })}>
              <SelectDisparador id="m-tipo" className="text-base">
                <SelectValor />
              </SelectDisparador>
              <SelectContenido>
                {catalogo.tiposEntregable.map((t) => (
                  <SelectOpcion key={t.clave} value={t.clave}>
                    {t.nombre}
                  </SelectOpcion>
                ))}
              </SelectContenido>
            </Select>
          </Campo>
          <Campo etiqueta="Lo da" htmlFor="m-area">
            <Select value={m.areaProductoraId ?? SIN} onValueChange={(v) => setM({ ...m, areaProductoraId: v === SIN ? null : v })}>
              <SelectDisparador id="m-area" className="text-base">
                <SelectValor placeholder="Área" />
              </SelectDisparador>
              <SelectContenido>
                <SelectOpcion value={SIN}>Sin área</SelectOpcion>
                {catalogo.areas.map((a) => (
                  <SelectOpcion key={a.id} value={a.id}>
                    {a.nombre}
                  </SelectOpcion>
                ))}
              </SelectContenido>
            </Select>
          </Campo>
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          <Campo etiqueta="Vence" htmlFor="m-fecha" error={mala ? "Debe ser anterior a la publicación." : undefined}>
            <Input id="m-fecha" type="date" max={sumarDias(fechaPublicacion, -1)} value={m.fechaLimite ?? ""} onChange={(e) => setM({ ...m, fechaLimite: e.target.value || null })} className="text-base" />
          </Campo>
          <Campo etiqueta="Para la tarea" htmlFor="m-tarea">
            <Select value={m.tareaId ?? SIN} onValueChange={(v) => setM({ ...m, tareaId: v === SIN ? null : v })}>
              <SelectDisparador id="m-tarea" className="text-base">
                <SelectValor placeholder="Opcional" />
              </SelectDisparador>
              <SelectContenido>
                <SelectOpcion value={SIN}>Toda la campaña</SelectOpcion>
                {tareas.map((t) => (
                  <SelectOpcion key={t.id} value={t.id}>
                    {t.titulo}
                  </SelectOpcion>
                ))}
              </SelectContenido>
            </Select>
          </Campo>
        </div>
        <DialogPie>
          <Boton type="button" variante="contorno" onClick={onCerrar}>
            Cancelar
          </Boton>
          <Boton type="submit" variante="acento" disabled={guardando || !m.nombre.trim() || mala}>
            {guardando ? "Guardando…" : "Pedir material"}
          </Boton>
        </DialogPie>
      </form>
    </>
  );
}

type EntregableLiga = { id: string; nombre: string; urlDrive: string | null };

interface PropsLiga {
  entregable: EntregableLiga;
  onCerrar: () => void;
  onGuardar: (url: string) => void;
  guardando: boolean;
}

export function DialogoLiga({ entregable, ...props }: Omit<PropsLiga, "entregable"> & { entregable: EntregableLiga | null }) {
  return (
    <Dialog open={Boolean(entregable)} onOpenChange={(o) => !o && props.onCerrar()}>
      <DialogContenido>{entregable && <FormularioLiga key={entregable.id} entregable={entregable} {...props} />}</DialogContenido>
    </Dialog>
  );
}

function FormularioLiga({ entregable, onCerrar, onGuardar, guardando }: PropsLiga) {
  const [url, setUrl] = React.useState(entregable.urlDrive ?? "");
  const valida = /^https?:\/\/\S+$/.test(url);
  return (
    <>
      <DialogEncabezado>
        <DialogTitulo>Entregar {entregable.nombre}</DialogTitulo>
        <DialogDescripcion>Pega la liga de Drive. Cada liga nueva sube la versión; Mercadotecnia valida o pide cambios.</DialogDescripcion>
      </DialogEncabezado>
      <form
        className="grid gap-4"
        onSubmit={(e) => {
          e.preventDefault();
          if (valida) onGuardar(url);
        }}
      >
        <Campo etiqueta="Liga" htmlFor="l-url" error={url && !valida ? "Debe empezar con https://" : undefined}>
          <Input id="l-url" type="url" inputMode="url" value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://drive.google.com/…" className="text-base" required />
        </Campo>
        <DialogPie>
          <Boton type="button" variante="contorno" onClick={onCerrar}>
            Cancelar
          </Boton>
          <Boton type="submit" variante="acento" disabled={guardando || !valida}>
            {guardando ? "Guardando…" : "Entregar"}
          </Boton>
        </DialogPie>
      </form>
    </>
  );
}
